/**
 * Billing domain types
 * Core types for the billing system based on story specifications
 * All monetary amounts are in minor currency units (e.g. rappen - 1 CHF = 100)
 */

import { DEFAULT_CURRENCY, formatCHF } from "@/lib/money";
// Deep import, not the `@/api` barrel: a great many tests replace the barrel wholesale
// with `vi.mock('@/api', () => ({ ... }))`, which would leave `InvoiceStatus` undefined at
// module load and blow up `ALLOWED_TRANSITIONS`' computed keys. The model module is a
// separate module id, so those mocks do not intercept it.
import { billing_UpdateInvoiceStatusRequest } from "@/api/models/billing_UpdateInvoiceStatusRequest";

// Import raw types for extension
import type {
  billing_InvoiceDTO,
  billing_InvoiceListResponse,
  billing_ServiceDetail,
} from "@/api";

// #1173: `export type { X as Y } from "@/api"` re-exports a name WITHOUT binding
// it locally, so the declarations below that reference `ApiBillingSettings`,
// `InvoiceLineItem` and `ServiceDetail` never resolved (TS2304) — which is why
// `interface BillingSettings extends ApiBillingSettings` silently inherited
// nothing. These are real local bindings; the re-export block stays intact so
// the module's public surface is unchanged.
import type {
  models_BillingSettings as ApiBillingSettings,
  models_EmailHoldRule,
  models_QRReferenceCutoverPreview,
  billing_InvoiceLineItemDTO as InvoiceLineItem,
  billing_ServiceDetail as ServiceDetail,
} from "@/api";

// Re-export generated types from API models
export type {
  billing_ContractResponse as Contract,
  billing_CreateContractRequest as CreateContractRequest,
  billing_UpdateContractRequest as UpdateContractRequest,
  billing_TerminateContractRequest as TerminateContractRequest,
  billing_ContractListResponse as ContractListResponse,
  billing_PayerResponse as ContractPayer,
  billing_PayerRequest as PayerRequest,
  billing_PayerSplitDTO as PayerSplit,
  billing_FamilyGroupPickerItemDTO as FamilyGroupPickerItem,
  billing_FamilyGroupPickerListResponse as FamilyGroupPickerListResponse,
  billing_ServicePatternResponse as ServicePatternDTO,
  billing_ServicePatternRequest as ServicePatternRequest,
  billing_ServiceDetail as ServiceDetail,
  billing_InvoiceListResponse as InvoiceListResponse,
  billing_InvoiceLineItemDTO as InvoiceLineItem,
  billing_InvoiceLineItemResponse as InvoiceLineItemResponse,
  billing_InvoiceSplit,
  billing_InvoiceSplitsResponse as InvoiceSplitsResponse,
  billing_GenerateInvoicesRequest as GenerateInvoicesRequest,
  // NOTE: GenerateInvoicesResponse exported separately with extended ValidationWarning types
  billing_UpdateInvoiceRequest as UpdateInvoiceRequest,
  billing_UpdateInvoiceStatusRequest as UpdateInvoiceStatusRequest,
  billing_PreviewInvoicesRequest as PreviewInvoicesRequest,
  // NOTE: PreviewInvoicesResponse exported separately with extended ValidationWarning types
  billing_InvoicePreview as InvoicePreview,
  // NOTE: DetailedInvoicePreview exported separately with extended ValidationWarning types
  billing_CancelContractVersionRequest as CancelContractVersionRequest,
  billing_UpdateContractVersionPeriodRequest as UpdateContractVersionPeriodRequest,
  billing_CreateTariffVersionRequest as CreateTariffVersionRequest,
  billing_TariffListResponse as TariffListResponse,
  billing_TariffRateRequest as TariffRateRequest,
  billing_TariffRateResponse as TariffRate,
  billing_BulkUpdateTariffRatesRequest as BulkUpdateTariffRatesRequest,
  billing_ValidateBillingRequest as ValidateBillingRequest,
  billing_ValidateBillingResponse as ValidateBillingResponse,
  billing_ValidationIssue as ValidationIssue,
  billing_DunningContextDTO,
  models_BillingSettings as ApiBillingSettings,
} from "@/api";

// Raw API invoice type (snake_case from backend)
// Use this type when working directly with API responses before transformation
export type InvoiceDTO = billing_InvoiceDTO;

// Transformed Contract type with camelCase fields for frontend use.
// This is the result of ContractTransformer.toFrontend()/arrayToFrontend(), which
// recases every key of the raw `Contract` (billing_ContractResponse) and parses
// startDate/endDate into Date objects.
//
// The date fields are a Date|string union because two kinds of producer feed this
// shape: the transformer (Date) and display summaries built straight off the raw
// response, e.g. ScheduleChangeDialog's `displayContract` (ISO string). Every
// consumer already handles both.
//
// Nested structures keep loose element types — no consumer of a transformed
// contract reads into them beyond `servicePattern`, and declaring recased copies
// of every nested generated model would duplicate the codegen.
/**
 * A label definition after `ContractTransformer.toFrontend` — the camelCased
 * `models_LabelDefinition`. Only the display fields are declared; the audit
 * columns come through untouched but nothing reads them off a contract.
 */
export interface TransformedContractLabel {
  color?: string;
  description?: string;
  entityType?: string;
  icon?: string;
  id?: string;
  isActive?: boolean;
  label?: string;
  sortOrder?: number;
}

/**
 * A contract payer after `ContractTransformer.toFrontend`. The transformer
 * recurses into the `payers` array (fieldTransformers.ts:220), so every key of
 * the wire `billing_PayerResponse` arrives camelCased, and the deprecated
 * `isPrimary` key is stripped.
 */
export interface TransformedContractPayer {
  contactEmail?: string;
  contactId?: string;
  contactName?: string;
  familyGroupId?: string;
  id?: string;
  payerType?: string;
  percentageShare?: number;
}

/**
 * The status the API computes for a contract version from its dates and
 * lifecycle (`effective_status`, models/contract.go). Upper case on the wire.
 */
export type ContractEffectiveStatus = 'IN_EFFECT' | 'UPCOMING' | 'EXPIRED' | 'CANCELLED' | 'TERMINATED';

export interface TransformedContract {
  id?: string;
  activeFrom?: string;
  activeTo?: string;
  beitragsfaktor?: number;
  beitragsfaktorValidUntil?: string;
  /** Subsidy case record (#2001), per contract version. ISO dates. */
  beitragsfaktorValidFrom?: string;
  sbuValidUntil?: string;
  subsidyApplicationFiledOn?: string;
  subsidyDecisionOn?: string;
  subsidyDecisionNumber?: string;
  /** KmbB supplement (#2007): the flag and the end of its yearly cycle (ISO date). */
  kmbb?: boolean;
  kmbbValidUntil?: string;
  /** Non-blocking warning codes of a write, e.g. SUBSIDY_KMBB_GROUP_LIMIT. */
  warnings?: string[];
  /** SBU day count (1..5), derived from the contracted weekdays. */
  attendanceDaysPerWeek?: number;
  /**
   * Not exposed by `billing_ContractResponse` today (the column exists in the
   * DB but no billing DTO serialises it), so this is always undefined at
   * runtime until the backend adds it. Declared because consumers read it.
   */
  billingCycle?: string;
  billingEndDate?: string;
  billingStartDate?: string;
  childId?: string;
  childInternalId?: string;
  childName?: string;
  /** The contract's childcare; placement fallback for org-wide groups (#1685). */
  childcareId?: string;
  contractGroupId?: string;
  /**
   * Not exposed by `billing_ContractResponse` today (the `contract_number`
   * column exists in the DB but no billing DTO serialises it), so this is
   * always undefined at runtime until the backend adds it. See the flagged
   * InvoiceCard read in the batch-2 PR description.
   */
  contractNumber?: string;
  createdAt?: string;
  createdBy?: string;
  /**
   * Not exposed by `billing_ContractResponse` today (the column exists in the
   * DB but no billing DTO serialises it), so this is always undefined at
   * runtime until the backend adds it.
   */
  currency?: string;
  discounts?: unknown[];
  effectiveStatus?: ContractEffectiveStatus;
  endDate?: Date | string;
  estimatedMonthlyAmount?: number;
  groupEndDate?: string;
  /** List views only: any non-deleted version of the group has a staff-written notes line (#1748). */
  groupHasStaffNotes?: boolean;
  groupStartDate?: string;
  /** List views only: any non-deleted version of the group waits for a subsidy decision (#1748). */
  groupSubsidyDecisionPending?: boolean;
  labels?: TransformedContractLabel[];
  notes?: string;
  overlapWarning?: unknown;
  parentVersionId?: string;
  payers?: TransformedContractPayer[];
  servicePattern?: Record<string, unknown>;
  siblingDiscountManual?: boolean;
  startDate?: Date | string;
  status?: string;
  subsidised?: boolean;
  subsidyAmount?: number;
  /**
   * Exact office share in Rappen for this contract version, taken at face value
   * (never pro-rated, never re-rounded). Overrides both the Beitragsfaktor
   * engine and the flat subsidyAmount. undefined = no override; 0 is meaningful
   * ("bill the office nothing").
   */
  subsidyAmountOverrideRappen?: number;
  subsidyDecisionPending?: boolean;
  subsidyOfficeId?: string;
  subsidySupplementAmount?: number;
  tariffName?: string;
  tariffVersions?: Array<Record<string, unknown>>;
  updatedAt?: string;
  updatedBy?: string;
  versionNumber?: number;
}

// What `useContracts` actually resolves to: a freshly built object carrying the
// transformed contracts plus the raw response's pagination counters. The counters
// keep their backend names because they are passed through untouched.
export interface TransformedContractListResponse {
  /** Always an array — empty when the API returns no `data`. */
  contracts: TransformedContract[];
  total_count?: number;
  limit?: number;
  offset?: number;
}

/**
 * A single selected service in the contract/allocation service-pattern grid.
 *
 * This is NOT the wire shape: `ServicePatternGrid` builds these objects itself
 * (`{ serviceCode, startTime, endTime, code }`), so the camelCase keys are real
 * at runtime while `billing_ServiceDetail` only declares `code`/`daily_rate`/
 * `group_type`/`name`. Extends ServiceDetail so a raw detail can widen into it.
 */
export interface ServicePatternEntry extends billing_ServiceDetail {
  serviceCode: string;
  startTime?: string;
  endTime?: string;
  groupType?: string;
  service_id?: string;
}

/**
 * A service-pattern entry parsed back out of a stored contract payload, where
 * the code may legitimately be missing: `normalizeServiceDetail` in
 * `ContractDetailsTab` resolves it from four possible keys and can come up
 * empty. Structurally identical to `ServicePatternEntry` otherwise, so a fully
 * populated entry widens into it.
 */
export type PartialServicePatternEntry = Omit<
  ServicePatternEntry,
  "serviceCode"
> & {
  serviceCode?: string;
};

/**
 * Camel-cased input of the contract terminate request, before
 * `ContractTransformer.toBackend` snake-cases it onto the wire. The wire type
 * stays `TerminateContractRequest` (`reason` + `termination_date`); this is the
 * pre-transform shape only.
 *
 * `terminationDate` is a `Date | string` union because the hook normalises a
 * `Date` with `format(...)` while `ContractsPage` already passes an ISO string.
 */
export interface TerminateContractInput {
  reason: string;
  terminationDate?: Date | string;
}

// Transformed Invoice type with camelCase fields for frontend use
// This is the result of InvoiceTransformer.toFrontend()
export interface Invoice {
  id: string;
  invoiceNumber: string;
  orgId: string;
  billingYear: number;
  billingMonth: number;
  // Optional: a DRAFT invoice has neither yet, and the wire DTO
  // (`billing_InvoiceDTO.issue_date` / `.due_date`) declares both optional.
  issueDate?: string;
  dueDate?: string;
  childId: string;
  childName: string;
  // Human-readable child id (e.g. "0451"), populated from the invoice list/detail
  // join. Optional so older payloads (and tests) without it still type-check.
  childInternalId?: string;
  // Austrittsdatum: the latest contract end date across all of the child's
  // contract versions and groups (`billing_InvoiceDTO.child_exit_date`, list
  // enrichment). Absent while the child still has an open-ended contract.
  childExitDate?: string;
  // Paying family: the reconciliation debtor account behind the invoice's parent
  // split (largest share on multi-payer invoices), resolved by the list
  // enrichment. Absent for parent-facing responses and when no account resolves.
  debtorAccountId?: string;
  debtorDisplayName?: string;
  contractId: string;
  contractNumber?: string;
  status: string;
  currency: string;
  totalAmount: number;
  paidAmount?: number; // Rappen; reconciled from invoice_splits.paid_amount
  /**
   * When the invoice was fully settled (`paid_at`,
   * internal/handlers/billing/invoice_response_dto.go:41). Absent while unpaid
   * and on the slim list DTO.
   */
  paidAt?: string;
  subtotalAmount: number;
  serviceDays: number;
  siblingDiscountAmount: number;
  emailManualHold?: boolean;
  /**
   * Payment reference (`billing_InvoiceResponse.payment_reference`, camel-cased by
   * `InvoiceTransformer.toFrontend`). Optional: the slim list DTO
   * (`billing_InvoiceDTO`) does not carry it.
   */
  paymentReference?: string;
  /**
   * Free-text operator note (`models.Invoice.Notes`). Not on the list DTO
   * (`billing_InvoiceDTO`), so it is only populated on detail responses and
   * after an update — read it defensively.
   */
  notes?: string;
  // Rounding snapshot (issue #720): the four rounding settings resolved from the
  // period-start tariff version at generation time. Undefined on legacy invoices.
  // Amounts are the rounding step in Rappen (minor units).
  roundingParentsEnabled?: boolean;
  roundingParentsAmount?: number;
  roundingSubsidyEnabled?: boolean;
  roundingSubsidyAmount?: number;
  // Revision tracking (flexible invoice corrections, Phase 2). `revision` starts
  // at 1; a parent-affecting correction on a finalized/sent invoice bumps it and
  // sets `needsResend=true` until the operator re-sends the corrected revision.
  revision?: number;
  needsResend?: boolean;
  createdAt: string;
  updatedAt: string;
  lineItems?: InvoiceLineItem[];
  payerSplits?: Array<{
    contactId?: string;
    contactName?: string;
    percentageShare?: number;
    amount?: number;
    paidAmount?: number; // Rappen; per-split reconciled amount
    debtorAccountId?: string;
  }>;
  adjustments?: Array<{
    type?: string;
    description?: string;
    percentage?: number;
    amount?: number;
    // Structured subvention breakdown (admin display only) — present on subsidy
    // adjustments from invoices generated after the breakdown was added. Keys are
    // camelCased by the field transformer (API sends snake_case).
    breakdown?: SubsidyBreakdown;
  }>;
}

/**
 * What `useInvoices` / `useInvoicesInfinite` actually resolve to. The hooks
 * replace `data` in place with `InvoiceTransformer.arrayToFrontend(...)`, so
 * every element is a camelCased `Invoice`, not the raw `billing_InvoiceDTO`
 * the generated response type declares. The pagination counters pass through
 * untouched and keep their backend names.
 */
export interface TransformedInvoiceListResponse
  extends Omit<billing_InvoiceListResponse, "data"> {
  data?: Invoice[];
}

// SubsidyBreakdown mirrors the backend SubsidyBreakdown (camelCased). All fields
// optional because historic (pre-breakdown) invoices won't carry them.
export interface SubsidyBreakdown {
  beitragsfaktor?: number;
  tarifRappen?: number;
  elternbeitragRappen?: number;
  subventionsbetragRappen?: number;
  subsidySupplementRappen?: number;
  /** The KmbB Regelzuschlag billed to the office (#2007). */
  kmbbSupplementRappen?: number;
  subventionstage?: number;
  monatsSbu?: number;
  reductionMultiplier?: number;
  rateVersionMultiplier?: number;
  rateVersionId?: string;
  /**
   * True when the Subventionsbetrag came from the contract version's
   * exact-amount override rather than from either engine. Such a breakdown
   * carries no beitragsfaktor / tarifRappen / elternbeitragRappen, so without
   * this marker the panel misreads it as a supplement-only adjustment.
   */
  amountOverride?: boolean;
}

// Invoice overrides (flexible invoice corrections feature). Phase 2 adds the
// parent scope (overrides that move the parent invoice total) alongside the
// Phase 1 subsidy_office scope (overrides that move only the subsidy split).
export type OverrideScope = "subsidy_office" | "parent";

export type OverrideKind =
  | "subsidy_amount"
  | "subsidy_supplement"
  | "tarif"
  | "discount"
  | "extra"
  | "adhoc"
  | "parent_total";

export interface InvoiceOverride {
  id: string;
  orgId: string;
  invoiceId: string;
  scope: OverrideScope;
  kind: OverrideKind;
  valueCents: number;
  computedCents: number;
  reason?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt: string;
}

// Immutable revision snapshot of an invoice (flexible invoice corrections,
// Phase 2). One row is archived each time a parent-affecting correction bumps
// the invoice's revision. Mirrors billing_InvoiceRevisionDTO (camelCase).
export interface InvoiceRevision {
  id: string;
  orgId: string;
  invoiceId: string;
  revision: number;
  status: string;
  totalAmount: number; // Rappen
  subsidyTotal?: number; // Rappen
  figures?: Record<string, unknown>;
  pdfObjectKey?: string;
  reason?: string;
  createdBy: string;
  createdAt: string;
}

// #1173: the local string-union `type` conflicted with the generated enum on
// `models_BillingSettings.email_hold_rules`. The generated model wins — it is
// the wire — so this is now an alias, keeping existing importers compiling.
export type EmailHoldRule = models_EmailHoldRule;

// Answer of GET /organisations/{id}/billing-settings/qr-reference-cutover (#1756):
// how many stored QR references a BESR-ID change recomputes, and whether a
// delivered invoice email locks the change.
export type QRReferenceCutoverPreview = models_QRReferenceCutoverPreview;

// #1173: only the JSONB extension fields that are genuinely ABSENT from
// `models_BillingSettings` are declared here. `services`, `email_auto_send_day`,
// `email_hold_rules` and `exit_fees` all exist on the generated model and are
// inherited — the local overrides conflicted with it (TS2430) and were the
// reason this interface inherited nothing at all.
export interface BillingSettings extends ApiBillingSettings {
  // Bank/Payment details for QR Bill generation (JSONB extension)
  iban?: string;
  qrIban?: string;
  // 6-digit BESR-ID; when set, QR references are built per household (#1756).
  besrId?: string;
  bankName?: string;
  creditorName?: string;
  creditorStreet?: string;
  creditorHouseNumber?: string;
  creditorPostalCode?: string;
  creditorCity?: string;
  creditorCountry?: string;
  // Scheduled sending configuration
  email_auto_send?: boolean;
  email_auto_send_time?: string;
  email_auto_send_timezone?: string;
  // Admin auto-CC configuration
  email_auto_cc?: boolean;
  email_cc_address?: string;
  // Kindergarten-date configuration (JSONB extension). No admin surface edits
  // these today (#1185 deleted the unmounted OrganisationAgeSettings component).
  // `daycare_min_age_months` / `kindergarten_min_age_months` ARE on the generated
  // model; these two are not.
  use_cantonal_kindergarten_dates?: boolean;
  custom_kindergarten_start_date?: string | null;
}

// Multi-group validation types
export interface ServiceConflict {
  day: string;
  serviceCode: string;
  serviceName: string;
  conflictingGroups: string[];
}

export interface ValidationResult {
  warnings: string[];
  errors: string[];
  conflicts: ServiceConflict[];
}

// Invoice validation warning types
export interface ValidationWarning {
  code: string;
  message: string;
  severity: "info" | "warning" | "critical";
  affectedEntity?: string;
  // Subject identity (issue #849) — lets the warning render the child and the
  // contract version as links. Every field is optional: warnings that can't
  // resolve a subject (e.g. an extra day outside any contract version) omit
  // them and fall back to plain text.
  childId?: string;
  childName?: string;
  childInternalId?: string;
  /** Contract VERSION id — the /contracts/:id/edit target. */
  contractId?: string;
  contractNumber?: string;
  // Settling-in hold detail (#1697), set only on SETTLING_IN_NOT_CLOSED: the
  // open plan holding the contract's invoices (link target /settling-in/:id)
  // and the closure step(s) it still lacks.
  settlingInPlanId?: string;
  settlingInMissingStep?: SettlingInMissingStep;
  // SETTLING_IN_FIRST_PERIOD_MISSING (#1697): the "YYYY-MM" period to generate first.
  settlingInFirstInvoicePeriod?: string;
  // SETTLING_IN_STALE_DRAFT (#1697): the stale draft invoice to cancel and its
  // "YYYY-MM" period.
  settlingInDraftInvoiceId?: string;
  settlingInDraftInvoiceNumber?: string;
  settlingInDraftPeriod?: string;
}

/** Closure step(s) an open settling-in plan still lacks (#1697). */
export type SettlingInMissingStep = "completion" | "sign_off" | "both";

// Extended types that override auto-generated API types with structured ValidationWarning
// Import the raw auto-generated types
import type {
  billing_GenerateInvoicesResponse as RawGenerateInvoicesResponse,
  billing_DetailedInvoicePreview as RawDetailedInvoicePreview,
  billing_PreviewInvoicesResponse as RawPreviewInvoicesResponse,
} from "@/api";

// Override GenerateInvoicesResponse to use ValidationWarning[] instead of string[]
export interface GenerateInvoicesResponse extends Omit<
  RawGenerateInvoicesResponse,
  "warnings"
> {
  warnings?: ValidationWarning[];
}

/**
 * The camelCase view of `billing_GenerateInvoicesResponse` produced by
 * `InvoiceTransformer.toFrontend` (which recursively snake_case -> camelCase's
 * every key, nested objects included). #1156: `useInvoices` used to declare the
 * snake_case `GenerateInvoicesResponse` here and then read `createdCount` /
 * `totalAmount` / `failedCount` off it — three keys that exist on neither shape.
 */
export interface TransformedGenerateInvoicesResponse {
  failedContracts?: Array<Record<string, unknown>>;
  invoices?: Array<Record<string, unknown>>;
  message?: string;
  skippedContracts?: Array<Record<string, unknown>>;
  success?: boolean;
  summary?: {
    contractsProcessed?: number;
    errors?: string[];
    siblingDiscountsApplied?: number;
    /** Minor units (Rappen). Never rounded or scaled here. */
    totalAmount?: number;
    totalInvoices?: number;
  };
  totalContracts?: number;
  totalCreated?: number;
  totalExcluded?: number;
  totalGenerated?: number;
  totalSkipped?: number;
  totalUpdated?: number;
  validationErrors?: string[];
  warnings?: ValidationWarning[];
}

// Override DetailedInvoicePreview to include ValidationWarning[]
export interface DetailedInvoicePreview extends Omit<
  RawDetailedInvoicePreview,
  "warnings"
> {
  warnings?: ValidationWarning[];
}

/**
 * Preview response as the UI receives it. `usePreviewInvoices` runs the raw
 * body through `InvoiceTransformer.toFrontend()`, whose `transformKeys` recases
 * every key recursively — so the camelCase aliases below are what actually
 * arrives. Consumers must NOT depend on that: `warnings` and `previews` stay
 * deliberately untyped at this boundary and must be funnelled through
 * `normalizeValidationWarnings()` before rendering (issue #1040; the SSE path
 * shipped this exact bug once already).
 */
export interface PreviewInvoicesResponse
  extends Omit<RawPreviewInvoicesResponse, "previews" | "warnings"> {
  totalContracts?: number;
  totalAmount?: number;
  previews?: unknown[];
  warnings?: unknown;
}

// Import the raw type for extension
import type { billing_TariffVersionResponse } from "@/api";

// Extended TariffVersion type that includes both snake_case and camelCase versions
// This is necessary because the transformer converts snake_case to camelCase
export interface TariffVersion extends billing_TariffVersionResponse {
  // Add camelCase versions of fields that are transformed
  groupType?: string;
  contractCount?: number;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  isDefault?: boolean;
  rateCount?: number;
  // Age-transition settings (camelCase versions produced by the transformer)
  ageTransitionMonths?: number;
  ageTransitionMonthsSubsidised?: number;
  ageTransitionTiming?: string;
  // Rounding settings (camelCase versions produced by the transformer)
  roundingParentsEnabled?: boolean;
  roundingParentsAmount?: number;
  roundingSubsidyEnabled?: boolean;
  roundingSubsidyAmount?: number;
}

// Contract effective status constants (computed from dates + lifecycle)
export const ContractStatus = {
  IN_EFFECT: "IN_EFFECT",
  UPCOMING: "UPCOMING",
  EXPIRED: "EXPIRED",
  CANCELLED: "CANCELLED",
  TERMINATED: "TERMINATED",
} as const;

export type ContractStatusType =
  (typeof ContractStatus)[keyof typeof ContractStatus];

// Invoice status constants.
//
// These are the generated enum from the API contract rather than a hand-written
// `as const` object: `UpdateInvoiceStatusRequest.status` is a TS *enum*, and a bare
// string literal is not assignable to a string enum, so every
// `useUpdateInvoiceStatus({ status: InvoiceStatus.X })` call site used to fail.
// The values are byte-identical to the previous literals.
export const InvoiceStatus = billing_UpdateInvoiceStatusRequest.status;

// Kept as the string-literal UNION (not the enum type) so the many existing
// comparisons against plain `string` invoice statuses keep compiling. Enum members
// are assignable to this union, so `InvoiceStatus.DRAFT` still satisfies it.
export type InvoiceStatusType = `${billing_UpdateInvoiceStatusRequest.status}`;

// Service type definition (dynamic from organization settings)
export interface Service {
  code: string;
  name: string;
  name_de?: string;
  name_en?: string;
  name_es?: string;
  name_fr?: string;
  name_it?: string;
  name_pt?: string;
  time_from: string;
  time_to: string;
  actual_days?: boolean;
  week_factor?: number;
  week_factor_subsidised?: number;
  active?: boolean;
  sort_order?: number;
}

// ServiceCodeType is now dynamic (any string from the organization's services)
export type ServiceCodeType = string;

// Group type constants for contracts
export const GroupType = {
  STANDARD: "STANDARD",
  SPECIAL: "SPECIAL",
} as const;

export type GroupTypeType = (typeof GroupType)[keyof typeof GroupType];

// Tariff version assignment for contracts
export interface TariffVersionAssignment {
  groupType: string;
  tariffVersionId: string;
}

// Days of the week for service patterns (1=Monday...7=Sunday)
export const DayOfWeek = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
} as const;

export type DayOfWeekType = 1 | 2 | 3 | 4 | 5 | 6 | 7;

// Helper functions to convert between integer weekdays and day names
export const getDayNameFromNumber = (dayNumber: DayOfWeekType): string => {
  const dayNames: Record<DayOfWeekType, string> = {
    1: "monday",
    2: "tuesday",
    3: "wednesday",
    4: "thursday",
    5: "friday",
    6: "saturday",
    7: "sunday",
  };
  return dayNames[dayNumber];
};

export const getDayNumberFromName = (dayName: string): DayOfWeekType | null => {
  const dayNumbers: Record<string, DayOfWeekType> = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7,
  };
  return dayNumbers[dayName.toLowerCase()] || null;
};

// Helper type for creating a new contract (client-side)
export interface ContractFormData {
  childId: string;
  tariffVersions: TariffVersionAssignment[];
  startDate: Date | string;
  endDate: Date | string;
  subsidised: boolean;
  subsidyOfficeId?: string;
  beitragsfaktor?: number; // 0 ≤ BF ≤ 1 (BF=0 → city covers 100% of subsidisable portion)
  beitragsfaktorValidUntil?: Date | string;
  notes?: string;
  servicePattern: {
    [key in DayOfWeekType]?: ServiceDetail[];
  };
  payers: Array<{
    contactId: string;
    paymentPercentage: number; // Must be integer, sum to 100
  }>;
}

// Helper type for invoice generation parameters
export interface InvoiceGenerationParams {
  year: number;
  month: number;
  contractIds?: string[];
  dryRun?: boolean;
}

// Helper type for retrying failed invoice generation
export interface RetryFailedInvoicesRequest {
  year: number;
  month: number;
  retryContractIds: string[];
}

// Bulk invoice runs (POST /api/v1/invoices/finalize-bulk and
// /api/v1/invoices/regenerate-bulk, SSE; hidden from OpenAPI, so these are
// hand-written). Exactly one form: invoiceIds, or periodYear + periodMonth
// (every draft of the period).
export interface BulkInvoiceSelection {
  invoiceIds?: string[];
  periodYear?: number;
  periodMonth?: number;
}

export interface BulkInvoiceProgress {
  current: number;
  total: number;
}

export type BulkFinalizeRequest = BulkInvoiceSelection;

export type BulkFinalizeProgress = BulkInvoiceProgress;

// One draft the run could not finalise. `reason` / `httpStatus` are exactly
// what PUT /invoices/{id}/status answers for the same error.
export interface BulkFinalizeFailureItem {
  invoiceId: string;
  invoiceNumber: string;
  childName: string;
  reason: string;
  httpStatus: number;
}

export interface BulkFinalizeResult {
  requested: number;
  finalized: number;
  failed: number;
  skipped: number;
  failures: BulkFinalizeFailureItem[];
}

export type BulkRegenerateRequest = BulkInvoiceSelection;

// One draft the bulk regenerate could not regenerate. `reason` / `code` /
// `httpStatus` are exactly what POST /invoices/{id}/regenerate answers for the
// same error; `code` is '' when the single path sends none.
export interface BulkRegenerateFailureItem {
  invoiceId: string;
  invoiceNumber: string;
  childName: string;
  reason: string;
  code: string;
  httpStatus: number;
}

export interface BulkRegenerateResult {
  requested: number;
  regenerated: number;
  failed: number;
  skipped: number;
  failures: BulkRegenerateFailureItem[];
}

// Helper type for tariff creation/update
export interface TariffFormData {
  name: string;
  description?: string;
  rates: Array<{
    groupType: GroupTypeType;
    serviceCode: string; // Dynamic service code from organization settings
    rateInRappen: number; // Amount in Rappen
  }>;
}

// Helper functions for monetary values
export const formatRappen = (
  rappen: number,
  currency: string = DEFAULT_CURRENCY,
): string => {
  const amount = rappen / 100;
  return formatCHF(amount, true, currency);
};

/**
 * The family group a contract's payers step works on (contract wizard and the
 * contract details edit form). `id` set means an existing family account; no
 * `id` means a new family group is created on save.
 */
export interface ContractFamilyGroupDraft {
  id?: string;
  familyName: string;
  billingType: "UNIFIED" | "SPLIT";
  unifiedBillingEmail?: string;
  members: Array<{
    contactId: string;
    paymentPercentage?: number;
  }>;
  /**
   * True once the user picked an existing family account or chose "create a
   * new family account". The wizard step remounts on every step change; this
   * flag stops the automatic shared-parent preselect from overriding the
   * user's choice when they come back to the step.
   */
  accountChoiceMade?: boolean;
}

// Validation helpers
export const validatePayerPercentages = (
  payers: Array<{ percentage_share?: number }>,
): boolean => {
  const total = payers.reduce(
    (sum, payer) => sum + (payer.percentage_share || 0),
    0,
  );
  return total === 100;
};

/**
 * True when at least one day of a service pattern carries services.
 *
 * The parameter is `object` rather than `ContractFormData["servicePattern"]`
 * because two equivalent pattern shapes exist: the form-data shape keyed by the
 * numeric `DayOfWeekType` (1..7) and the Zod schema shape in
 * `contractSchema.ts` keyed by day name (`monday`..`sunday`). The check is
 * key-agnostic — it only inspects the values.
 */
export const validateServicePattern = (pattern: object): boolean => {
  // At least one day must have services
  return Object.values(pattern).some(
    (services) => Array.isArray(services) && services.length > 0,
  );
};

// Default values for forms
export const getDefaultContractFormData = (): Partial<ContractFormData> => ({
  subsidised: false,
  subsidyOfficeId: undefined,
  beitragsfaktor: undefined,
  beitragsfaktorValidUntil: undefined,
  servicePattern: {},
  payers: [],
});

export const getDefaultTariffFormData = (): Partial<TariffFormData> => ({
  rates: [],
});

// Enhanced billing types for Phase 2 integration
export type AgeType = "BABY" | "CHILD";

export interface BillingPeriodInfo {
  year: number;
  month: number;
  start: string;
  end: string;
}

export interface AgePeriodSummary {
  startDate: string;
  endDate: string;
  ageType: AgeType;
  days: number;
  lineItems: InvoiceLineItem[];
  amount: number; // In Rappen (Swiss cents)
  discountAmount?: number; // In Rappen (Swiss cents)
  finalAmount?: number; // In Rappen (Swiss cents)
}

export interface EnhancedBillingSummary {
  contractId: string;
  childId: string;
  billingPeriod: BillingPeriodInfo;
  agePeriods: AgePeriodSummary[];
  hasTransition: boolean;
  transitionDate?: string;
  transitionReason: string;
  totalAmount: number; // In Rappen (Swiss cents)
  totalDiscountAmount?: number; // In Rappen (Swiss cents)
  finalAmount?: number; // In Rappen (Swiss cents)
}

// Extended cost calculation types for enhanced summary
export interface PeriodCostBreakdown {
  startDate: string;
  endDate: string;
  ageType: AgeType;
  days: number;
  daily: Record<
    string,
    {
      services: string[];
      amount: number;
      details?: Array<{ group: string; services: string[]; amount: number }>;
    }
  >;
  weekly: number;
  monthly: number;
  hasTransition: boolean;
}

export interface EnhancedMultiGroupCostResult {
  totalCostBreakdown: {
    totalAmount: number;
    totalAmountBeforeDiscounts?: number;
    periods: PeriodCostBreakdown[];
    hasTransition: boolean;
    transitionDate?: string;
    transitionReason?: string;
  };
  groupCostBreakdowns: Record<
    string,
    {
      totalAmount: number;
      weekly: number;
      daily: Record<string, { services: string[]; amount: number }>;
      periods: PeriodCostBreakdown[];
    }
  >;
  warnings: string[];
}

// Discount integration types
export interface WeekdayDiscount {
  [day: number]: number; // day 1-5 (Mon-Fri), amount in Rappen
}

export interface CustomDiscount {
  id?: string;
  name: string;
  type: "percentage" | "amount";
  // For 'amount', value is stored in minor currency units (rappen). A NEGATIVE
  // amount is a surcharge that ADDS to the parent total (percentages stay >= 0).
  value: number;
}

export interface DiscountsData {
  weekday?: WeekdayDiscount;
  custom?: CustomDiscount[];
}

// Contract calculation request for enhanced API
export interface ContractCalculationRequest {
  childId: string;
  servicePattern: {
    [groupType: string]: Record<
      string,
      Array<{ service_id: string; serviceCode?: string }>
    >;
  };
  tariffVersions: Record<string, string>;
  startDate?: string;
  endDate?: string;
  discounts?: DiscountsData;
  calculationDate?: string; // For age transition calculations
}
